import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, EMPTY } from 'rxjs';
import { catchError, filter, switchMap, take, finalize } from 'rxjs/operators';
import { AuthService } from '../_services/auth.service';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshSubject = new BehaviorSubject<string | null>(null);
  private readonly LOGOUT_SIGNAL = '__LOGOUT__';
  private redirectingToLogin = false;

  constructor(private auth: AuthService, private router: Router) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const url = req.url.toLowerCase();
    const apiBase = environment.apiBaseUrl.toLowerCase();

    const isApiCall = url.startsWith(apiBase);

    const isAuthCall =
      url.includes('/auth/login') ||
      url.includes('/auth/register') ||
      url.includes('/auth/refresh') ||
      url.includes('/auth/confirm-pin') ||
      url.includes('/auth/resend-pin');

    const accessToken = this.auth.accessToken;
    const tokenType = this.auth.tokenType ?? 'Bearer';

    // ✅ ONLY attach token to your backend API
    const authReq =
      isApiCall && !isAuthCall && accessToken
        ? req.clone({ setHeaders: { Authorization: `${tokenType} ${accessToken}` } })
        : req;

    return next.handle(authReq).pipe(
      catchError((err: HttpErrorResponse) => {
        const errUrl = (err.url ?? req.url).toLowerCase();
        const isRefreshCall = errUrl.includes('/auth/refresh');

        // If refresh fails → session expired
        if (err.status === 401 && isRefreshCall) {
          this.handleSessionExpired();
          return EMPTY;
        }

        // ✅ Only handle 401 for API calls (not S3, etc.)
        if (err.status === 401 && isApiCall && !isAuthCall) {
          return this.handle401(authReq, next);
        }

        return throwError(() => err);
      })
    );
  }

  private handle401(req: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshSubject.next(null);

      return this.auth.refreshAccessToken().pipe(
        switchMap((t) => {
          const newAccessToken = t.accessToken;
          const newTokenType = t.tokenType ?? 'Bearer';

          this.refreshSubject.next(newAccessToken);

          return next.handle(
            req.clone({ setHeaders: { Authorization: `${newTokenType} ${newAccessToken}` } })
          );
        }),
        catchError((e) => {
          this.refreshSubject.next(this.LOGOUT_SIGNAL);
          this.handleSessionExpired();
          return throwError(() => e);
        }),
        finalize(() => (this.isRefreshing = false))
      );
    }

    return this.refreshSubject.pipe(
      filter((t): t is string => t !== null),
      take(1),
      switchMap((token) => {
        if (token === this.LOGOUT_SIGNAL) {
          return EMPTY;
        }

        const tokenType = this.auth.tokenType ?? 'Bearer';
        return next.handle(req.clone({ setHeaders: { Authorization: `${tokenType} ${token}` } }));
      })
    );
  }

  private handleSessionExpired() {
    this.auth.forceLogout(this.auth.isCurrentRouteProtected());
  }
}
